import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { detectProject, EProjectNames, PROJECT_DATA } from './useProjectData';

const SESSIONS_KEY = 'platformSessions';
const SELECTED_PLATFORM_KEY = 'selectedPlatform';

export const MULTI_PLATFORMS: EProjectNames[] = [
  EProjectNames.TTELD,
  EProjectNames.EVO,
  EProjectNames.ONTIME,
  EProjectNames.ZIPPY,
  EProjectNames.SBAELD,
  EProjectNames.RTE,
  EProjectNames.RULER,
  EProjectNames.RRELD,
  EProjectNames.PROTRUX,
  EProjectNames.FIVEELD,
  EProjectNames.DELTAELD,
];

interface PlatformSession {
  token: string;
  user?: any;
  expires?: string;
}

type SessionsMap = Partial<Record<EProjectNames, PlatformSession>>;
export type PingStatus = 'checking' | 'online' | 'offline';

function readSessions(): SessionsMap {
  try {
    return JSON.parse(localStorage.getItem(SESSIONS_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeSessions(sessions: SessionsMap) {
  localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
}

/**
 * The admin's active session (flat `token`/`user`/`expires` keys) always belongs to
 * whichever platform was hostname-detected or last switched to. On first use we seed
 * that session into the multi-platform vault so it shows online without re-login.
 */
function readSessionsWithSeed(): SessionsMap {
  const sessions = readSessions();
  const hostPlatform = detectProject();
  const flatToken = localStorage.getItem('token');

  if (flatToken && MULTI_PLATFORMS.includes(hostPlatform) && !sessions[hostPlatform]) {
    sessions[hostPlatform] = {
      token: flatToken,
      user: JSON.parse(localStorage.getItem('user') || 'null'),
      expires: localStorage.getItem('expires') || undefined,
    };
    writeSessions(sessions);
  }

  if (!localStorage.getItem(SELECTED_PLATFORM_KEY) && MULTI_PLATFORMS.includes(hostPlatform)) {
    localStorage.setItem(SELECTED_PLATFORM_KEY, hostPlatform);
  }

  return sessions;
}

export function getSelectedPlatform(): EProjectNames | null {
  const stored = localStorage.getItem(SELECTED_PLATFORM_KEY) as EProjectNames | null;
  return stored && MULTI_PLATFORMS.includes(stored) ? stored : null;
}

async function pingPlatform(code: EProjectNames, token: string): Promise<boolean> {
  try {
    const res = await axios.get('notifications/admin/unread-count', {
      baseURL: PROJECT_DATA[code].baseUrl,
      headers: { Authorization: token },
      timeout: 8000,
    });
    return res.status >= 200 && res.status < 300;
  } catch {
    return false;
  }
}

export function usePlatformSessions() {
  const [sessions, setSessions] = useState<SessionsMap>(() => readSessionsWithSeed());
  const [statuses, setStatuses] = useState<Partial<Record<EProjectNames, PingStatus>>>({});
  const [selected, setSelected] = useState<EProjectNames | null>(() => getSelectedPlatform());

  const pingAll = useCallback(async () => {
    const current = readSessionsWithSeed();
    setSessions(current);
    setSelected(getSelectedPlatform());
    setStatuses((prev) => {
      const next = { ...prev };
      MULTI_PLATFORMS.forEach((code) => {
        next[code] = current[code]?.token ? 'checking' : 'offline';
      });
      return next;
    });

    const entries = await Promise.all(
      MULTI_PLATFORMS.map(async (code) => {
        const session = current[code];
        if (!session?.token) return [code, 'offline' as PingStatus] as const;
        const online = await pingPlatform(code, session.token);
        return [code, (online ? 'online' : 'offline') as PingStatus] as const;
      }),
    );

    setStatuses((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
  }, []);

  useEffect(() => {
    pingAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginToPlatform = useCallback(async (code: EProjectNames, email: string, password: string) => {
    const { data } = await axios.post(
      'auths/login/ra',
      { email, password },
      { baseURL: PROJECT_DATA[code].baseUrl, headers: { 'Content-Type': 'application/json' } },
    );
    const next = { ...readSessions(), [code]: { token: data.id, user: data.user, expires: data.ttl } };
    writeSessions(next);
    setSessions(next);
    setStatuses((prev) => ({ ...prev, [code]: 'online' }));
  }, []);

  const switchToPlatform = useCallback((code: EProjectNames, path?: string) => {
    const session = readSessions()[code];
    if (!session?.token) return;
    localStorage.setItem('token', session.token);
    if (session.user) localStorage.setItem('user', JSON.stringify(session.user));
    if (session.expires !== undefined) localStorage.setItem('expires', session.expires);
    localStorage.setItem(SELECTED_PLATFORM_KEY, code);
    document.location.replace(path || '/');
  }, []);

  return { sessions, statuses, selected, pingAll, loginToPlatform, switchToPlatform };
}
